import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import './YoutubeStoryteller.css';

const YoutubeStorytellerStep1 = ({ formData, onComplete }) => {
  const [localData, setLocalData] = useState({
    mainIdeas: formData.mainIdeas || '',
    selectedTags: formData.selectedTags || [],
    language: formData.language || 'german'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0); // 0 = not loading, 1-4 = steps
  const [apiError, setApiError] = useState(null);
  
  const auth = getAuth();

  // 21 essential influence tags (reduced by 30%)
  const influenceTags = [
    // Tone & Style (5)
    { id: 'inspirational', label: 'Inspirational', category: 'tone', emoji: '✨' },
    { id: 'educational', label: 'Educational', category: 'tone', emoji: '📚' },
    { id: 'entertaining', label: 'Entertaining', category: 'tone', emoji: '🎭' },
    { id: 'motivational', label: 'Motivational', category: 'tone', emoji: '💪' },
    { id: 'authentic', label: 'Authentic', category: 'tone', emoji: '🎯' },
    
    // Hook Types (4)
    { id: 'question-hook', label: 'Question Hook', category: 'hook', emoji: '❓' },
    { id: 'shocking-fact', label: 'Shocking Fact', category: 'hook', emoji: '😱' },
    { id: 'personal-story', label: 'Personal Story', category: 'hook', emoji: '📖' },
    { id: 'problem-solution', label: 'Problem/Solution', category: 'hook', emoji: '💡' },
    
    // Content Goals (5)
    { id: 'educate', label: 'Educate', category: 'goal', emoji: '🎓' },
    { id: 'inspire', label: 'Inspire', category: 'goal', emoji: '⭐' },
    { id: 'challenge-beliefs', label: 'Challenge Beliefs', category: 'goal', emoji: '🧠' },
    { id: 'build-trust', label: 'Build Trust', category: 'goal', emoji: '🤗' },
    { id: 'actionable-tips', label: 'Actionable Tips', category: 'goal', emoji: '✅' },
    
    // Format Types (4)
    { id: 'storytelling', label: 'Storytelling', category: 'format', emoji: '📚' },
    { id: 'step-by-step', label: 'Step-by-Step', category: 'format', emoji: '📝' },
    { id: 'tutorial', label: 'Tutorial', category: 'format', emoji: '🎓' },
    { id: 'real-talk', label: 'Real Talk', category: 'format', emoji: '💬' },
    
    // Emotional Elements (3)
    { id: 'uplifting', label: 'Uplifting', category: 'emotion', emoji: '🌟' },
    { id: 'empowering', label: 'Empowering', category: 'emotion', emoji: '💪' },
    { id: 'energetic', label: 'Energetic', category: 'emotion', emoji: '⚡' }
  ];

  const handleChange = (field, value) => {
    setLocalData({ ...localData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const toggleTag = (tagId) => {
    const isSelected = localData.selectedTags.includes(tagId);
    const newTags = isSelected
      ? localData.selectedTags.filter(id => id !== tagId)
      : [...localData.selectedTags, tagId];
    
    setLocalData({ ...localData, selectedTags: newTags });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!localData.mainIdeas.trim()) {
      newErrors.mainIdeas = 'Please enter at least one idea';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setLoadingStep(1);
    setApiError(null);

    // Simulate progress steps (since we can't get real-time feedback from Cloud Function)
    const progressTimer = setTimeout(() => {
      setLoadingStep(2);
      setTimeout(() => {
        setLoadingStep(3);
        setTimeout(() => {
          setLoadingStep(4);
        }, 3000); // Step 3 after 3s
      }, 8000); // Step 2 after 8s
    }, 10000); // Step 1 completes after 10s

    try {
      console.log('🎬 [YT-STORYTELLER] Calling Cloud Function...');
      console.log('📊 [YT-STORYTELLER] Data:', {
        mainIdeasLength: localData.mainIdeas.length,
        tagsCount: localData.selectedTags.length
      });

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateYoutubeStoryIdeas(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mainIdeas: localData.mainIdeas,
          selectedTags: localData.selectedTags,
          language: localData.language,
          userId: auth.currentUser?.uid || 'anonymous'
        })
      });

      clearTimeout(progressTimer);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to generate video ideas');
      }

      if (data.success && data.newsArticles && data.storyBlocks && data.narrativeStories) {
        console.log('✅ [YT-STORYTELLER] Success!');
        console.log('📰 [YT-STORYTELLER] News Articles:', data.newsArticles.length);
        console.log('🎬 [YT-STORYTELLER] Story Blocks:', data.storyBlocks.length);
        console.log('📖 [YT-STORYTELLER] Narrative Stories:', data.narrativeStories.length);
        console.log('💰 [YT-STORYTELLER] Total Cost:', data.usage.estimatedCost.totalCost, 'USD');
        console.log('💰 [YT-STORYTELLER] Step 1 Cost:', data.usage.step1?.cost?.totalCost || 'N/A', 'USD');
        console.log('💰 [YT-STORYTELLER] Step 2 Cost:', data.usage.step2?.cost?.totalCost || 'N/A', 'USD');
        console.log('💰 [YT-STORYTELLER] Step 3 Cost:', data.usage.step3?.cost?.totalCost || 'N/A', 'USD');
        
        // Pass NEW data structure to Step 2
        onComplete({
          ...localData,
          newsArticles: data.newsArticles,
          storyBlocks: data.storyBlocks,
          narrativeStories: data.narrativeStories,
          usage: data.usage
        });
      } else {
        throw new Error('Invalid response format from API');
      }

    } catch (error) {
      console.error('❌ [YT-STORYTELLER] Error:', error);
      setApiError(error.message || 'Failed to generate video ideas. Please try again.');
      clearTimeout(progressTimer);
    } finally {
      setLoading(false);
      setLoadingStep(0);
    }
  };

  return (
    <div className="yt-step1-container">

      <div className="yt-form">
        {/* Language Selection */}
        <div className="yt-form-group">
          <label className="yt-label">
            <i className="fas fa-globe"></i>
            Video Language
            <span className="yt-required">*</span>
          </label>
          <div className="yt-language-selector">
            <button
              type="button"
              className={`yt-language-btn ${localData.language === 'german' ? 'selected' : ''}`}
              onClick={() => handleChange('language', 'german')}
            >
              <span className="yt-lang-flag">🇩🇪</span>
              <span className="yt-lang-label">Deutsch</span>
            </button>
            <button
              type="button"
              className={`yt-language-btn ${localData.language === 'english' ? 'selected' : ''}`}
              onClick={() => handleChange('language', 'english')}
            >
              <span className="yt-lang-flag">🇬🇧</span>
              <span className="yt-lang-label">English</span>
            </button>
          </div>
          <span className="yt-hint">Choose the language for your video content</span>
        </div>

        {/* Main Ideas Field */}
        <div className="yt-form-group">
          <label className="yt-label">
            <i className="fas fa-pen-fancy"></i>
            Your Ideas & Thoughts
            <span className="yt-required">*</span>
          </label>
          <textarea
            className={`yt-textarea ${errors.mainIdeas ? 'error' : ''}`}
            rows="8"
            placeholder="E.g.: 'I want to talk about overcoming fear and taking action. Share some personal stories, add practical tips, maybe reference recent psychology research...'"
            value={localData.mainIdeas}
            onChange={(e) => handleChange('mainIdeas', e.target.value)}
            maxLength={3000}
          />
          {errors.mainIdeas && <span className="yt-error-text">{errors.mainIdeas}</span>}
          <div className="yt-hint-with-counter">
            <span className="yt-hint">Share your vision - we'll create building blocks for your video</span>
            <span className={`yt-char-count ${localData.mainIdeas.length > 2800 ? 'warning' : ''}`}>
              {localData.mainIdeas.length} / 3000
            </span>
          </div>
        </div>

        {/* Influence Tags */}
        <div className="yt-form-group">
          <label className="yt-label">
            <i className="fas fa-sliders-h"></i>
            Style & Direction
            <span className="yt-optional">(Optional - Select what fits your vision)</span>
          </label>
          <div className="yt-influence-tags">
            {influenceTags.map(tag => (
              <button
                key={tag.id}
                type="button"
                className={`yt-influence-tag ${localData.selectedTags.includes(tag.id) ? 'selected' : ''}`}
                onClick={() => toggleTag(tag.id)}
              >
                <span className="yt-tag-emoji">{tag.emoji}</span>
                <span className="yt-tag-label">{tag.label}</span>
              </button>
            ))}
          </div>
          <span className="yt-hint">
            {localData.selectedTags.length > 0 
              ? `${localData.selectedTags.length} selected - These will influence the AI's suggestions`
              : 'Click to select styles, hooks, goals, and formats that match your vision'
            }
          </span>
        </div>

        {/* API Error Message */}
        {apiError && (
          <div className="yt-error-banner">
            <i className="fas fa-exclamation-circle"></i>
            <span>{apiError}</span>
          </div>
        )}

        {/* Loading Progress Indicator */}
        {loading && (
          <>
            <div className="yt-loading-backdrop"></div>
            <div className="yt-loading-progress">
            <div className="yt-progress-steps">
              {/* Step 1: News */}
              <div className={`yt-progress-step ${loadingStep >= 1 ? 'active' : ''} ${loadingStep > 1 ? 'completed' : ''}`}>
                <div className="yt-progress-step-icon">
                  {loadingStep > 1 ? (
                    <i className="fas fa-check"></i>
                  ) : (
                    <i className="fas fa-newspaper"></i>
                  )}
                </div>
                <div className="yt-progress-step-content">
                  <div className="yt-progress-step-title">Finding News & Trends</div>
                  <div className="yt-progress-step-desc">Searching breaking stories</div>
                </div>
              </div>

              {/* Step 2: Story Blocks */}
              <div className={`yt-progress-step ${loadingStep >= 2 ? 'active' : ''} ${loadingStep > 2 ? 'completed' : ''}`}>
                <div className="yt-progress-step-icon">
                  {loadingStep > 2 ? (
                    <i className="fas fa-check"></i>
                  ) : (
                    <i className="fas fa-cube"></i>
                  )}
                </div>
                <div className="yt-progress-step-content">
                  <div className="yt-progress-step-title">Building Story Blocks</div>
                  <div className="yt-progress-step-desc">Creating techniques & strategies</div>
                </div>
              </div>

              {/* Step 3: Narratives */}
              <div className={`yt-progress-step ${loadingStep >= 3 ? 'active' : ''} ${loadingStep > 3 ? 'completed' : ''}`}>
                <div className="yt-progress-step-icon">
                  {loadingStep > 3 ? (
                    <i className="fas fa-check"></i>
                  ) : (
                    <i className="fas fa-book-open"></i>
                  )}
                </div>
                <div className="yt-progress-step-content">
                  <div className="yt-progress-step-title">Writing Narratives</div>
                  <div className="yt-progress-step-desc">Crafting emotional stories</div>
                </div>
              </div>

              {/* Step 4: Virality Analysis */}
              <div className={`yt-progress-step ${loadingStep >= 4 ? 'active' : ''} ${loadingStep > 4 ? 'completed' : ''}`}>
                <div className="yt-progress-step-icon">
                  {loadingStep > 4 ? (
                    <i className="fas fa-check"></i>
                  ) : (
                    <i className="fas fa-chart-line"></i>
                  )}
                </div>
                <div className="yt-progress-step-content">
                  <div className="yt-progress-step-title">Analyzing Virality</div>
                  <div className="yt-progress-step-desc">Rating all 30 blocks for impact</div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="yt-progress-bar-container">
              <div 
                className="yt-progress-bar-fill" 
                style={{ width: `${(loadingStep / 4) * 100}%` }}
              ></div>
            </div>

            <div className="yt-progress-info">
              <span className="yt-progress-text">
                {loadingStep === 1 && '⏱️ Step 1-3 running in parallel... (~15-20s)'}
                {loadingStep === 2 && '🚀 Almost there... (~10s)'}
                {loadingStep === 3 && '🎬 Final touches... (~5s)'}
                {loadingStep === 4 && '✨ Analyzing virality scores...'}
              </span>
              <span className="yt-progress-percent">{Math.round((loadingStep / 4) * 100)}%</span>
            </div>
          </div>
          </>
        )}

        {/* Submit Button */}
        <div className="yt-form-actions">
          <button 
            type="button" 
            className="yt-btn-primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Generating... {Math.round((loadingStep / 4) * 100)}%
              </>
            ) : (
              <>
                <i className="fas fa-robot"></i>
                Generate with AI
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default YoutubeStorytellerStep1;

